import React, { useEffect } from 'react';
import LegalSidebar from '../components/LegalSidebar';
import { setPageSEO } from '../utils/seo.js';
import './Terms.css';

function CookiePolicy() {
  useEffect(() => setPageSEO({
    title: 'Cookie Policy - Clientalio',
    description: 'Understand how Clientalio uses cookies and similar technologies to improve your browsing experience and website performance.',
    canonical: 'https://clientalio.com/cookie-policy',
    ogTitle: 'Cookie Policy - Clientalio',
    ogDescription: 'Understand how Clientalio uses cookies and similar technologies to improve your browsing experience and website performance.',
    twitterTitle: 'Cookie Policy - Clientalio',
    twitterDescription: 'Understand how Clientalio uses cookies and similar technologies to improve your browsing experience and website performance.'
  }), []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <div className="terms-page">
      <div className="container">
        <div className="terms-header">
          <h1>Cookie Policy</h1>
          <p className="last-updated">Last Updated: 06-Oct-2025</p>
        </div>
        <div className="terms-layout">
          <LegalSidebar activePage="cookie-policy" />

          {/* Main Content */}
          <div className="terms-content">
            <div className="content-section">

              <section className="terms-section">
                <h2>1. Introduction</h2>
                <p>
                  At Clientalio, we're all about transparency — including how we use cookies. This
                  Cookie Policy explains what cookies are, how we use them on our website, and
                  your choices regarding their use.
                </p>
              </section>

              <section className="terms-section">
                <h2>2. What Are Cookies?</h2>
                <p>
                  Cookies are tiny text files stored on your device (computer, smartphone, tablet)
                  when you visit a website. They help sites remember your preferences, understand
                  how you use the site, and provide a better user experience.
                </p>
                <p>
                  Cookies can be "session cookies" (deleted when you close your browser) or
                  "persistent cookies" (remain on your device for a set period).
                </p>
              </section>

              <section className="terms-section">
                <h2>3. How We Use Cookies</h2>
                <p>
                  We use cookies for the following purposes:
                </p>
                <ul>
                  <li><strong>Essential Cookies:</strong> Required for the website to function properly, including login authentication and session management</li>
                  <li><strong>Preference Cookies:</strong> Remember your settings and preferences for a personalized experience</li>
                  <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our site, which pages are popular, and how to improve our service</li>
                  <li><strong>Performance Cookies:</strong> Monitor site performance and identify technical issues</li>
                </ul>
                <p>
                  We do not use cookies to collect personal data without your consent or sell your
                  information to third parties.
                </p>
              </section>

              <section className="terms-section">
                <h2>4. Types of Cookies We Use</h2>
                <p>
                  <strong>First-Party Cookies:</strong> Set by Clientalio directly
                </p>
                <ul>
                  <li>Authentication and session management</li>
                  <li>User preferences and settings</li>
                  <li>Security features</li>
                </ul>
                <p>
                  <strong>Third-Party Cookies:</strong> Set by external services
                </p>
                <ul>
                  <li>Google Analytics for usage statistics</li>
                  <li>Social media plugins (if applicable)</li>
                  <li>Payment processing services</li>
                </ul>
              </section>

              <section className="terms-section">
                <h2>5. Managing Cookies</h2>
                <p>
                  You have control over cookies and can manage or delete them through your browser
                  settings. Most browsers allow you to:
                </p>
                <ul>
                  <li>View what cookies are stored and delete them individually</li>
                  <li>Block third-party cookies</li>
                  <li>Block all cookies from specific websites</li>
                  <li>Block all cookies from being set</li>
                  <li>Delete all cookies when you close your browser</li>
                </ul>
                <p>
                  <strong>Note:</strong> Disabling cookies may affect your experience on our website
                  and limit certain features or functionality.
                </p>
              </section>

              <section className="terms-section">
                <h2>6. Browser-Specific Instructions</h2>
                <p>
                  To manage cookies in popular browsers:
                </p>
                <ul>
                  <li><strong>Chrome:</strong> Settings → Privacy and security → Cookies and other site data</li>
                  <li><strong>Firefox:</strong> Settings → Privacy & Security → Cookies and Site Data</li>
                  <li><strong>Safari:</strong> Preferences → Privacy → Manage Website Data</li>
                  <li><strong>Edge:</strong> Settings → Cookies and site permissions → Manage and delete cookies</li>
                </ul>
              </section>

              <section className="terms-section">
                <h2>7. Third-Party Cookies</h2>
                <p>
                  We may use third-party tools like Google Analytics or Meta Pixel to understand
                  user behavior and improve our services. These partners comply with GDPR standards
                  and may use cookies to collect anonymous visit data.
                </p>
                <p>
                  You can opt out of Google Analytics by installing the
                  <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer"> Google Analytics Opt-out Browser Add-on</a>.
                </p>
              </section>

              <section className="terms-section">
                <h2>8. Updates to Cookie Policy</h2>
                <p>
                  We may update this Cookie Policy from time to time to reflect changes in
                  technology, legislation, or our practices. Changes will be posted on this page
                  with an updated "Last Updated" date.
                </p>
              </section>

              <section className="terms-section">
                <h2>9. Contact Us</h2>
                <p>
                  If you have questions about our use of cookies:
                </p>
                <ul>
                  <li>Email: <a href="mailto:support@clientalio.com">support@clientalio.com</a></li>
                  <li>Legal: <a href="mailto:legal@clientalio.com">legal@clientalio.com</a></li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CookiePolicy;
